import { useRef } from 'react';
import clsx from 'clsx';
import type { RefObject } from 'react';
import styles from './DataAvailabilityModal.module.scss';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import Modal from '../../../../../common/_custom/Modal/Modal';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import { useDataAvailabilityLogic } from '../hooks/useDataAvailabilityLogic';
import { useDataAvailabilityModalA11y } from '../hooks/useDataAvailabilityModalA11y';
import { DATA_AVAILABILITY_COPY as COPY } from '../constants/reconciliationsCopy';
import {
    DATE_FORMATS,
    formatDateByFormat,
    getFinancialYear,
    getLastCompletedMonthKey,
    monthKeyToStartDate,
} from '../../../../../utils/dateUtils';
import CategorySelector from '../../../../../common/CategorySelector/CategorySelector';
import closeIcon from '../../../../../common/_custom/Modal/close.svg';
import bottomAngleIcon from '../../../../../assets/bottomAngleIcon.svg';
import { GREEN_CHECK_SMALL } from '../../../../../assets/hostedassets';
import { toTitleCaseWithSeparators } from '../../../../../utils/utils';
import { toCoverageSegments, toMonthSegments } from './availabilitySegments';
import type { DataAvailability, DataAvailabilityGstRow, MonthFlags } from '../../../../../store/reconciliations/types';

const TITLE_ID = 'data-availability-title';
const FY_SELECT_ID = 'data-availability-fy';

interface DataAvailabilityModalProps {
    orgId: string;
    onClose: () => void;
    /** The button that opened the modal — focus returns to it on close. */
    triggerRef: RefObject<HTMLElement>;
}

const monthLabel = (month: string) => formatDateByFormat(monthKeyToStartDate(month), DATE_FORMATS.PERIOD_MONTH_YEAR);
const connectionLabel = (connection: DataAvailabilityGstRow['connection']) => {
    if (connection === 'NOT_CONNECTED') return COPY.NOT_CONNECTED;
    return connection === 'DISCONNECTED' ? COPY.DISCONNECTED : COPY.CONNECTED;
};

function GstStatusLabel({ label }: Readonly<{ label: string }>) {
    return <span className={styles.GstStatusLabel}>{label}</span>;
}

function MonthCells({
    months,
    flags,
    withInfo,
    missingLabel,
}: Readonly<{ months: string[]; flags: MonthFlags; withInfo?: boolean; missingLabel?: string | null }>) {
    return (
        <>
            {toMonthSegments(months, flags, getLastCompletedMonthKey()).map((segment) => {
                if (segment.kind === 'present') {
                    const square = (
                        <span className={styles.CheckSquare} role="img" aria-label={COPY.AVAILABLE}>
                            <img src={GREEN_CHECK_SMALL} alt="" />
                        </span>
                    );
                    return (
                        <td key={segment.month} className={styles.MonthCell}>
                            {withInfo ? (
                                <Tooltip content={COPY.BANK_CELL_INFO} direction="top" usePortal fitContent>
                                    {square}
                                </Tooltip>
                            ) : (
                                square
                            )}
                        </td>
                    );
                }
                if (segment.kind === 'future') {
                    return (
                        <td key={segment.month} className={styles.MonthCell}>
                            <span className={styles.MonthBox} role="img" aria-label={COPY.MONTH_FUTURE} />
                        </td>
                    );
                }
                const isSingleMonth = segment.months.length === 1;
                if (missingLabel) {
                    return (
                        <td key={segment.months[0]} colSpan={segment.months.length} className={styles.MonthCell}>
                            {isSingleMonth ? (
                                <span
                                    className={clsx(styles.GstStatusChip, styles.IconOnly)}
                                    role="img"
                                    aria-label={missingLabel}
                                    title={missingLabel}
                                />
                            ) : (
                                <span className={styles.GstStatusChip} title={missingLabel}>
                                    <GstStatusLabel label={missingLabel} />
                                </span>
                            )}
                        </td>
                    );
                }
                return segment.months.map((month) => (
                    <td key={month} className={styles.MonthCell}>
                        <span className={styles.MonthBox} role="img" aria-label={COPY.NOT_AVAILABLE} />
                    </td>
                ));
            })}
        </>
    );
}

function RowLabel({ name, id, idTitle }: Readonly<{ name: string | null; id: string | null; idTitle?: string }>) {
    return (
        <th scope="row" className={styles.RowLabel} aria-label={idTitle && id ? [name, id, idTitle].filter(Boolean).join(', ') : undefined}>
            <div className={styles.RowLabelContent}>
                {name !== null && <span className={styles.RowName}>{name}</span>}
                {id !== null && (
                    <span className={styles.RowId} title={idTitle}>
                        {id}
                    </span>
                )}
            </div>
        </th>
    );
}

function AvailabilityGrid({
    months,
    sectionLabel,
    emptyNote,
    children,
}: Readonly<{ months: string[]; sectionLabel: string; emptyNote?: string; children?: React.ReactNode }>) {
    return (
        <section className={styles.Section}>
            <table className={styles.Grid}>
                <thead>
                    <tr>
                        <th scope="col" className={styles.SectionLabel}>
                            {sectionLabel}
                        </th>
                        {months.map((month) => (
                            <th key={month} scope="col" className={styles.MonthHead}>
                                {monthLabel(month)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>{children}</tbody>
            </table>
            {emptyNote && <div className={styles.EmptyNote}>{emptyNote}</div>}
        </section>
    );
}

function StatementRow({ label, months, source }: Readonly<{ label: string; months: string[]; source: DataAvailability['audited'] }>) {
    const segments = toCoverageSegments(months, source.fys ?? []);
    return (
        <div className={styles.StatementRow}>
            <span className={styles.SectionLabel}>{label}</span>
            <div className={styles.CoverageGrid}>
                {segments.map((segment) =>
                    segment.kind === 'covered' ? (
                        // Dynamic run length — the one value a stylesheet cannot know.
                        <span
                            key={segment.months[0]}
                            className={styles.AvailableChip}
                            style={{ gridColumn: `span ${segment.months.length}` }}
                        >
                            <img src={GREEN_CHECK_SMALL} alt="" />
                            {COPY.AVAILABLE}
                        </span>
                    ) : (
                        <span key={segment.month} className={styles.MonthBox} role="img" aria-label={COPY.NOT_AVAILABLE} />
                    ),
                )}
            </div>
        </div>
    );
}

/** The merged GST / bank month grids and the Audited / Provisional coverage rows for one period. */
function AvailabilityBody({ data }: Readonly<{ data: DataAvailability }>) {
    const hasNoRows = !data.gst.length && !data.bank.length;
    return (
        <>
            {hasNoRows ? (
                <div className={styles.EmptyNote}>{COPY.EMPTY}</div>
            ) : (
                <AvailabilityGrid
                    months={data.months}
                    sectionLabel={COPY.GST_SECTION}
                    emptyNote={data.gst.length ? undefined : COPY.GST_EMPTY}
                >
                    {data.gst.map((row) => {
                        // The Revenue popup's exact label call (GstConnectionPopup.tsx).
                        const statusLabel = row.isPrimary ? null : toTitleCaseWithSeparators(row.status) || null;
                        const showStatusChip = statusLabel !== null && !Object.values(row.months).some(Boolean);
                        return (
                            <tr key={row.gstin}>
                                <RowLabel
                                    name={row.state ? toTitleCaseWithSeparators(row.state) : null}
                                    id={row.gstin}
                                    idTitle={row.connection ? connectionLabel(row.connection) : statusLabel ?? undefined}
                                />
                                {showStatusChip ? (
                                    <td colSpan={data.months.length} className={styles.MonthCell}>
                                        <span className={styles.GstStatusChip}>
                                            <GstStatusLabel label={statusLabel} />
                                        </span>
                                    </td>
                                ) : (
                                    <MonthCells months={data.months} flags={row.months} missingLabel={statusLabel} />
                                )}
                            </tr>
                        );
                    })}
                </AvailabilityGrid>
            )}
            {data.bank.length > 0 && (
                <>
                    <hr className={styles.SectionDivider} />
                    <AvailabilityGrid months={data.months} sectionLabel={COPY.BANK_GROUP}>
                        {data.bank.map((account) => (
                            <tr key={account.accountId}>
                                <RowLabel name={account.bank ?? COPY.BANK_NAME_PLACEHOLDER} id={account.accountNumber} />
                                <MonthCells months={data.months} flags={account.months} withInfo />
                            </tr>
                        ))}
                    </AvailabilityGrid>
                </>
            )}
            <hr className={styles.SectionDivider} />
            <StatementRow label={COPY.AUDITED} months={data.months} source={data.audited} />
            <hr className={styles.SectionDivider} />
            <StatementRow label={COPY.PROVISIONAL} months={data.months} source={data.provisional} />
        </>
    );
}

function DataAvailabilityModal({ orgId, onClose, triggerRef }: Readonly<DataAvailabilityModalProps>) {
    const dialogRef = useRef<HTMLDivElement>(null);
    const { entry, lastData, shownPeriod, fyOptions, periodOptions, selectedPeriodId, selectFy, selectPeriod, retry } =
        useDataAvailabilityLogic(orgId);
    const isLoading = !entry || entry.status === 'loading';
    const isRefreshing = isLoading && lastData !== null;
    useDataAvailabilityModalA11y({ dialogRef, triggerRef, onClose });

    return (
        <Modal open persistent onClose={onClose} size="flexible" withoutCloseButton modalClassName={styles.Dialog}>
            <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={TITLE_ID} tabIndex={-1} className={styles.Content}>
                <div className={styles.Header}>
                    <h2 id={TITLE_ID} className={styles.Title}>
                        {COPY.TITLE}
                    </h2>
                    <span className={styles.HeaderDivider} aria-hidden="true" />
                    <div className={styles.FyGroup}>
                        <label htmlFor={FY_SELECT_ID} className={styles.FyLabel}>
                            {COPY.FY_LABEL}
                        </label>
                        <span className={styles.FySelectWrap}>
                            <select
                                id={FY_SELECT_ID}
                                className={styles.FySelect}
                                value={shownPeriod.fy === null ? '' : String(shownPeriod.fy)}
                                onChange={(e) => selectFy(Number(e.target.value))}
                            >
                                <option value="" disabled hidden>
                                    {COPY.FY_LABEL}
                                </option>
                                {fyOptions.map((fy) => (
                                    <option key={fy} value={fy}>
                                        {getFinancialYear(fy, true)}
                                    </option>
                                ))}
                            </select>
                            <img src={bottomAngleIcon} alt="" aria-hidden="true" className={styles.FyChevron} />
                        </span>
                    </div>
                    <CategorySelector
                        size="small"
                        categoryOptions={periodOptions}
                        selectedCategory={selectedPeriodId}
                        changeSelectedTab={selectPeriod}
                    />
                    <button type="button" aria-label={COPY.CLOSE} className={styles.CloseButton} onClick={onClose}>
                        <img src={closeIcon} alt="" />
                    </button>
                </div>
                <div className={styles.BodyFrame}>
                    {isLoading && (
                        <div role="status" aria-live="polite" className={clsx(styles.LoadingState, isRefreshing && styles.LoadingOverlay)}>
                            <LoadingSpinner height="2.5rem" color="var(--primary-text-colour)" style={{ margin: 0 }} />
                            <span className={styles.SrOnly}>{COPY.LOADING}</span>
                        </div>
                    )}
                    <div className={styles.Body}>
                        {isRefreshing && lastData && (
                            <div className={styles.RefreshingGrid} aria-busy="true">
                                <AvailabilityBody data={lastData} />
                            </div>
                        )}
                        {entry?.status === 'error' && (
                            <div className={styles.EmptyNote}>
                                {COPY.UNAVAILABLE}{' '}
                                <button type="button" className={styles.RetryButton} onClick={retry}>
                                    {COPY.RETRY}
                                </button>
                            </div>
                        )}
                        {entry?.status === 'success' && entry.data && <AvailabilityBody data={entry.data} />}
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default DataAvailabilityModal;
